// a library to wrap and simplify api calls
import apisauce, { ApiResponse } from 'apisauce';
import debugConfig from '../../../config/debug.config';
import fixtureApiService from './fixture.api.service';
import { SERVER_BASE_URL } from '../../../config/routes-config';
import { ErrorInfo, isErrorInfo } from '../../../interfaces/error-info.interface';
import { UserAuthModel } from '../../../models/user-auth.model';
import {
  getUserAccessToken,
  getUserRefreshToken,
  cleanupUserData,
  setUserToken
} from '../../helpers/auth-helper/auth-helper.service';
import { ResponseModel } from '../../../interfaces/response-model.interface';
import { AreaModel } from '../../../models/area.model';
import { PropertyModel } from '../../../models/property.model';
import { BulkUpdate, Expenses, UnitTypeModel } from '../../../models/unit-types.model';
import { AmenityModel } from '../../../models/amenity.model';
import { UserModel } from '../../../models/user.model';
import {
  BookingRequestModel,
  BookingRequestModelData
} from '../../../models/booking-request.model';
import {
  ReservationModel,
  CreateReservationRequestModel,
  ReservationModelData
} from '../../../models/reservation.model';
import { ReservationDetailsModel } from '../../../models/reservation-details.model';
import {
  CreateBlockDatesModel,
  BlockDatesModel,
  UpdateBlockDatesModel
} from '../../../models/block-dates.model';
import { GuestModel } from '../../../models/guest.model';
import { GuestUiModel } from '../../../ui-models/guest.ui-model';
import { ReservationDetailsUiModel } from '../../../ui-models/reservation.ui-model';
import { PaymentLinkModel, PaymentUIModel } from '../../../ui-models/payment.ui-model';
import { PaymentModel, PaymentModelData, PaymentSearch } from '../../../models/payment.model';
import {
  BookAvailabilityResponseModel,
  BookOfferResponseModel
} from '../../../ui-models/book.ui-model';
import { UnitTypeGroupModel } from '../../../models/unit-type-group..model';
import { ReservationUpdates } from '../../../ui-models/reservation-updates.ui-model';
import { OccupancyResponseModel } from '../../../ui-models/occupancy.ui-model';
import { UserNotificationsModel } from '../../../models/user-notifications.model';
import { CreateExpenses } from '../../../../UnitTypeModule/containers/edit-unit-type/expenses-list/components/create-reservation/create-expense-modal.component';
import { CreatePayOutDto, Payout, SearchPayOutDto } from '../../../models/payout.model';
import { ReservationActivityModel } from '../../../models/reservation-activity.model';
import { AmenityChannelModel } from '../../../models/amenity-channel.model';
import { ConfigurationModel } from '../../../models/configuration.model';
import { ConfigurationUiModel } from '../../../ui-models/configuration.ui.model';
import { CompanyModel } from '../../../models/company.model';
import { FinancialReportQueryModel } from '../../../models/financial-report-query.model';
import { UserType } from '../../../enums/user-type.enum';
import { userRefreshToken } from '../../data/auth.data';
import {
  UnitTypeUiModelResponse,
  RoomsUiModelResponse
} from '../../../ui-models/unit-type.ui-model';
import { KPIChartsSearch } from '../../../models/kpis.model';
import { FeedbackPayload, FeedbackResponse } from '../../../models/feedbacks.model';
import type { PackageModel, FurnishingModel } from '../../../../FurnishingModule/types';

const create = (baseURL = SERVER_BASE_URL) => {
  const api = apisauce.create({
    baseURL,
    headers: {
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json'
    },
    timeout: 240000
  });

  api.addRequestTransform(async (request) => {
    request.httpsAgent = {
      rejectUnauthorized: false
    };
    const jwt = getUserAccessToken();
    if (jwt) request.headers.Authorization = `Bearer ${jwt}`;
  });

  api.addAsyncResponseTransform(async (response) => {
    if (
      response.status === 401 &&
      response.config?.url &&
      response.config?.url?.indexOf('/auth') === -1
    ) {
      const refreshToken = await getUserRefreshToken();
      if (refreshToken) {
        const userData = await userRefreshToken(refreshToken);

        if (isErrorInfo(userData)) {
          cleanupUserData();
        } else {
          setUserToken({ ...userData, refreshToken });
          const accessToken = userData.accessToken;
          if (accessToken) api.setHeader('Authorization', `Bearer ${accessToken}`);
          const data = await api.any(response.config as any);
          response.data = data.data;
          response.status = data.status;
          response.problem = data.problem;
          response.headers = data.headers;
          response.ok = data.ok;
        }
      } else {
        cleanupUserData();
      }
    }
  });

  // ==================== API Functions ==================

  const getRoot = () => api.get('');

  // User & Auth APIs
  const registerUser = (
    email: string,
    password: string
  ): Promise<ApiResponse<UserAuthModel, ErrorInfo>> =>
    api.post('/auth/register', { email, password });

  const loginUser = (
    email: string,
    password: string,
    token: string | undefined | null
  ): Promise<ApiResponse<UserAuthModel, ErrorInfo>> =>
    api.post('/auth/login', { email, password, token });

  const changePassword = (
    token: string,
    password: string
  ): Promise<ApiResponse<UserAuthModel, ErrorInfo>> =>
    api.post('/auth/changePassword', { token, password });

  const forgotPassword = (email: string): Promise<ApiResponse<{}, ErrorInfo>> =>
    api.post('/auth/forgotPassword', { email });

  const validateToken = (): Promise<ApiResponse<{}, ErrorInfo>> => api.get('/auth/verifyToken');

  const refreshToken = (refreshToken: string): Promise<ApiResponse<UserAuthModel, ErrorInfo>> =>
    api.post('/auth/refreshToken', { refreshToken });

  const getUserNotifications = (): Promise<
    ApiResponse<ResponseModel<UserNotificationsModel>, ErrorInfo>
  > => api.get('/notifications/attention');

  // Area APIs
  const getAreas = (): Promise<ApiResponse<ResponseModel<AreaModel[]>, ErrorInfo>> =>
    api.get('/areas/');

  const getCites = (): Promise<
    ApiResponse<ResponseModel<{ id: string; name: string }[]>, ErrorInfo>
  > => api.get('/cities');

  const createArea = (
    areaData: AreaModel
  ): Promise<ApiResponse<ResponseModel<AreaModel>, ErrorInfo>> => api.post('/areas/', areaData);

  const updateArea = (
    areaId: string,
    areaData: AreaModel
  ): Promise<ApiResponse<ResponseModel<AreaModel>, ErrorInfo>> =>
    api.put('/areas/' + areaId, areaData);

  const deleteArea = (areaId: string): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.delete('/areas/' + areaId);

  const uploadDefaultImage = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<AreaModel>, ErrorInfo>> =>
    api.post(`/areas/uploadDefaultImage`, formData);

  const uploadSponsoredImage = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<AreaModel>, ErrorInfo>> =>
    api.post(`/areas/uploadSponsoredImage`, formData);

  // Property APIs
  const getProperties = (): Promise<ApiResponse<ResponseModel<PropertyModel[]>, ErrorInfo>> =>
    api.post('/properties/search', {});

  const getProperty = (
    propertyId: string
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.get('/properties/' + propertyId);

  const createProperty = (
    propertyData: PropertyModel
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.post('/properties/', propertyData);

  const updateProperty = (
    propertyId: string,
    propertyData: Partial<PropertyModel>
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.put('/properties/' + propertyId, propertyData);

  const deleteProperty = (propertyId: string): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.delete('/properties/' + propertyId);

  const uploadPropertyAsset = (
    formData: FormData,
    propertyId: string
  ): Promise<ApiResponse<{ data: any }, ErrorInfo>> =>
    api.post(`/properties/uploadAsset/${propertyId}`, formData);

  const uploadPropertyCoverImage = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.post('/properties/uploadCover', formData);

  const uploadCompoundDetails = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.post('/properties/uploadCompound', formData);

  const deleteCompoundDetails = (
    propertyId: string,
    id: string
  ): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.delete(`/properties/${propertyId}/compound/${id}`);

  const updateCompoundDetails = (
    propertyId: string,
    id: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.put(`/properties/${propertyId}/compound/${id}`, formData);

  const addPropertyManager = (
    id: string,
    propertyId: string
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.post('properties/addPropertyManagers', { id, propertyId });

  const deletePropertyManager = (
    id: string,
    propertyId: string
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.delete(`properties/deletePropertyManagers?id=${id}&propertyId=${propertyId}`);

  // Unit Type APIs
  const createUnitType = (
    unitTypeData: UnitTypeModel
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post('/unitTypes/', unitTypeData);

  const getPropertyUnitTypes = (
    propertyId: string
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel[]>, ErrorInfo>> =>
    api.get(`/properties/${propertyId}/unitTypes`);

  const getUnassignedUnitTypes = (): Promise<
    ApiResponse<ResponseModel<UnitTypeModel[]>, ErrorInfo>
  > => api.get('/unitTypes/unassigned');

  const getUnitTypes = (
    limit: string,
    offset: string,
    query: {}
  ): Promise<ApiResponse<ResponseModel<UnitTypeUiModelResponse>, ErrorInfo>> =>
    api.post('/unitTypes/search', { limit, offset, ...query });

  const getAllRooms = (): Promise<ApiResponse<ResponseModel<RoomsUiModelResponse[]>, ErrorInfo>> =>
    api.get('/unitTypes/getAllRooms');

  const searchInUnitTypes = (data: {
    checkInDate?: number;
    checkOutDate?: number;
    landlordId?: string;
  }): Promise<ApiResponse<ResponseModel<UnitTypeModel[]>, ErrorInfo>> =>
    api.post('/unitTypes/search', data);

  const getUnitType = (
    unitTypeId: string
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.get(`/unitTypes/${unitTypeId}`);

  const updateUnitType = (
    unitTypeId: string,
    unitTypeData: Partial<UnitTypeModel>
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.put('/unitTypes/' + unitTypeId, unitTypeData);

  const bulkUpdateUnitType = (
    unitTypeData: Partial<UnitTypeModel[]>
  ): Promise<ApiResponse<ResponseModel<BulkUpdate>, ErrorInfo>> =>
    api.put('/unitTypes/bulkUpdate', unitTypeData);

  const getAllBulkUpdateUnitTypeData = (): Promise<
    ApiResponse<ResponseModel<BulkUpdate[]>, ErrorInfo>
  > => api.get('/unitTypes/bulkUpdate');

  const syncUnitTypeChannel = (
    unitTypeId: string
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post(`/unitTypes/${unitTypeId}/sync`, {});

  const deleteUnitTypeShowroomImage = (data: {
    unitTypeId: number;
    imageId: string;
    roomId: string;
  }): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post('/unitTypes/deleteShowRoom', data);

  const uploadUnitTypeShowRoomImage = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post('/unitTypes/uploadShowRoomImage', formData);

  const uploadUnitTypeImage = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post('/unitTypes/uploadImage', formData);

  const uploadBrandingImage = (
    unitTypeId: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post(`/unitTypes/${unitTypeId}/uploadBrandingImages`, formData);

  const deleteBrandingImage = (data: {
    unitTypeId: string;
    imageId: string;
  }): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.delete(`unitTypes/${data.unitTypeId}/brandingImages/${data.imageId}`);

  const deleteUnitTypeImage = (data: {
    unitTypeId: number;
    imageId: string;
  }): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post('/unitTypes/deleteImage', data);

  const addPropertyManagerToUnits = (
    id: string,
    unitTypeId: number
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post('unitTypes/addPropertyManagers', { userId: id, unitTypeId: String(unitTypeId) });

  const deletePropertyManagerToUnits = (
    id: string,
    unitTypeId: number
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.delete(`unitTypes/deletePropertyManagers?userId=${id}&unitTypeId=${unitTypeId}`);

  // Amenity APIs
  const getAmenities = (): Promise<ApiResponse<ResponseModel<AmenityModel[]>, ErrorInfo>> =>
    api.get('/amenities/');

  const getChannelAmenities = (): Promise<
    ApiResponse<ResponseModel<AmenityChannelModel[]>, ErrorInfo>
  > => api.get('/amenities/channel/list');

  const getChannelRoomAmenities = (): Promise<
    ApiResponse<ResponseModel<AmenityChannelModel[]>, ErrorInfo>
  > => api.get('/amenities/channel/roomList');

  // Company APIs
  const getCompanies = (): Promise<ApiResponse<ResponseModel<CompanyModel[]>, ErrorInfo>> =>
    api.get('/company');

  const createCompany = (
    companyData: CompanyModel
  ): Promise<ApiResponse<ResponseModel<CompanyModel>, ErrorInfo>> =>
    api.post('/company', companyData);

  const updateCompany = (
    companyId: string,
    companyData: Partial<CompanyModel>
  ): Promise<ApiResponse<ResponseModel<CompanyModel>, ErrorInfo>> =>
    api.put(`/company/${companyId}`, companyData);

  const deleteCompany = (companyId: string): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.delete(`/company/${companyId}`);

  const createAmenity = (
    amenityData: AmenityModel
  ): Promise<ApiResponse<ResponseModel<AmenityModel>, ErrorInfo>> =>
    api.post('/amenities/', amenityData);

  const updateAmenity = (
    amenityId: string,
    amenityData: AmenityModel
  ): Promise<ApiResponse<ResponseModel<AmenityModel>, ErrorInfo>> =>
    api.put('/amenities/' + amenityId, amenityData);

  const deleteAmenity = (amenityId: string): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.delete('/amenities/' + amenityId);

  const uploadAmenityImage = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<AmenityModel>, ErrorInfo>> =>
    api.post('/amenities/uploadImage', formData);

  // User Management APIs
  const getAllUsers = (): Promise<ApiResponse<ResponseModel<UserModel[]>, ErrorInfo>> =>
    api.get('/users/');

  const addUser = (
    userModel: UserModel
  ): Promise<ApiResponse<ResponseModel<UserModel>, ErrorInfo>> => api.post('/users/', userModel);

  const updateUser = (user: UserModel): Promise<ApiResponse<ResponseModel<UserModel>, ErrorInfo>> =>
    api.put(`/users`, user);

  const searchUser = (queryData: {
    role?: UserType[];
    phone?: string[];
    email?: string[];
  }): Promise<ApiResponse<ResponseModel<UserModel[]>, ErrorInfo>> =>
    api.post('/users/search', { query: queryData });

  const getUserLockStatus = (
    userId: string
  ): Promise<ApiResponse<ResponseModel<{ data: { isEmailLocked: boolean } }>, ErrorInfo>> =>
    api.get(`users/lockStatus/${userId}`);

  const unlockUser = (userId: string): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.delete(`users/unlock/${userId}`);

  const getUsers = (
    query: {},
    offset: number,
    limit: number
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post('/reservations/distinct-users', { query, offset, limit });

  // Booking APIs
  const getBookingRequests = (): Promise<
    ApiResponse<ResponseModel<BookingRequestModel[]>, ErrorInfo>
  > => api.get('/bookings/requests');

  const updateBookingRequests = (
    requestId: string,
    data: any
  ): Promise<ApiResponse<ResponseModel<BookingRequestModel[]>, ErrorInfo>> =>
    api.put(`/bookings/${requestId}`, data);

  const getBookingRequestsByQuery = (
    query: {},
    offset: number,
    limit: number
  ): Promise<ApiResponse<ResponseModel<BookingRequestModelData>, ErrorInfo>> =>
    api.post('/bookings/search', { query, offset, limit });

  const getUserBookings = (
    userId: string
  ): Promise<ApiResponse<ResponseModel<BookingRequestModel[]>, ErrorInfo>> =>
    api.post('bookings/bookingsByUserId', { userId });

  const getBookingRequestDetails = (
    requestId: string
  ): Promise<ApiResponse<ResponseModel<BookingRequestModel>, ErrorInfo>> =>
    api.get('/bookings/requests/' + requestId);

  const acceptBookingRequest = (
    requestId: string,
    roomId: string
  ): Promise<ApiResponse<ResponseModel<BookingRequestModel>, ErrorInfo>> =>
    api.post('/bookings/acceptRequest', { requestId, roomId });

  const rejectBookingRequest = (
    requestId: string,
    rejectionReason: string
  ): Promise<ApiResponse<ResponseModel<BookingRequestModel>, ErrorInfo>> =>
    api.post('/bookings/rejectRequest', { requestId, rejectionReason });

  const createNewBookingRequest = (data: any) => api.post('/bookings', data);

  const submitBookingMessage = (
    message: string,
    requestId: string
  ): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.post(`/bookings/requests/${requestId}/messages`, { message });

  const getBookAvailability = (query: { unitTypeId?: string }) =>
    api.get<ResponseModel<BookAvailabilityResponseModel[]>, ErrorInfo>(
      `bookings/availability/unitType/${query.unitTypeId}`
    );

  const getBookOffer = (query: {
    unitTypeId?: string;
    checkInDate: string;
    checkOutDate: string;
    discountAmount?: number;
    discountFees?: string[];
  }) =>
    api.post<ResponseModel<BookOfferResponseModel>, ErrorInfo>(
      `bookings/offer/unitType/${query.unitTypeId}`,
      query
    );

  // Reservation APIs
  const getReservations = (
    query: {},
    offset: number,
    limit: number,
    countOnly?: boolean
  ): Promise<ApiResponse<ResponseModel<ReservationModelData>, ErrorInfo>> =>
    api.post('/reservations/search', { query, offset, limit, countOnly });

  const getReservationGes = (payload: {
    range: 'TODAY' | 'ATTENTION-REQUIRED' | 'UPCOMING';
  }): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/reservations-ges', payload);

  const getCxQueue = (): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.get('/cx-queue');

  const updateCxQueue = (
    id: string,
    payload: {
      outcomeValue: string;
      issueType?: string;
    }
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.put(`/cx-queue/${id}`, payload);

  const uploadReservationGesInspectionImages = (
    reservationId: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post(`/reservations-ges/${reservationId}/inspectionImages`, formData);

  const getReservationsOperationsSearch = (
    query: {},
    offset: number,
    limit: number
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post('/reservations-operations/search', { query, offset, limit });

  const createReservationOperationMaintenance = (payload: {
    description: string;
    reservationId: number | string;
  }): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post('/maintenance-requests/', payload);

  const searchMaintenanceRequests = (
    query: {},
    offset: number,
    limit: number
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post('/maintenance-requests/search', { query, offset, limit });

  const updateMaintenanceRequest = (
    maintenanceRequestId: number | string,
    payload: {
      status?: 'new' | 'in_progress' | 'done';
      comment: string;
    }
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/maintenance-requests/${maintenanceRequestId}`, payload);

  const getOperationDashboardSummary = (): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.get('/operation-dashboard');

  const getDoubleBookings = (): Promise<
    ApiResponse<
      ResponseModel<
        {
          currentReservation: ReservationModel;
          otherReservation?: ReservationModel;
          blockedDate?: BlockDatesModel;
        }[]
      >,
      ErrorInfo
    >
  > => api.get('/reservations/doubleBookings');

  const getUserReservations = (
    userId: string
  ): Promise<ApiResponse<ResponseModel<ReservationModel[]>, ErrorInfo>> =>
    api.post('reservations/resevationsByUserId', { userId });

  const getOneReservation = (
    id: string
  ): Promise<ApiResponse<ResponseModel<ReservationDetailsModel>, ErrorInfo>> =>
    api.get(`/reservations/${id}`);

  const updateOneReservation = (
    reservation: Partial<ReservationDetailsUiModel>
  ): Promise<ApiResponse<ResponseModel<ReservationDetailsModel>, ErrorInfo>> =>
    api.put(`/reservations/${reservation.id}`, reservation);

  const updateReservationGes = (
    reservationId: string,
    payload: {
      actualCheckInAt?: number;
      actualCheckOutAt?: number;
      isUnitInspected?: boolean;
      isUnitCleaned?: boolean;
    }
  ): Promise<ApiResponse<ResponseModel<ReservationModel>, ErrorInfo>> =>
    api.put(`/reservations/${reservationId}`, payload);

  const updateReservationDates = (
    reservationId: string,
    dates: { checkInDate: number; checkOutDate: number }
  ): Promise<ApiResponse<ResponseModel<ReservationDetailsModel>, ErrorInfo>> =>
    api.put(`/reservations/${reservationId}/dates`, dates);

  const createReservations = (reservation: CreateReservationRequestModel) =>
    api.post<ResponseModel<ReservationModel>, ErrorInfo>('reservations', reservation);

  const reactivateReservation = (reservationId: string, roomId: string) =>
    api.post<ResponseModel<ReservationModel>, ErrorInfo>(
      `reservations/${reservationId}/reActivate`,
      { roomId }
    );

  const postReservationMessage = (
    reservationId: string,
    message: string
  ): Promise<ApiResponse<ResponseModel<ReservationDetailsModel>, ErrorInfo>> =>
    api.post(`/reservations/${reservationId}/message`, { message });

  const getReservationActivities = (
    reservationId: string
  ): Promise<ApiResponse<ResponseModel<ReservationActivityModel[]>, ErrorInfo>> =>
    api.get(`/reservations/${reservationId}/activities`);

  const getPaymentActivities = (
    paymentId: string
  ): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get(`/payments/${paymentId}/changes`);
  const getReservationChanges = (
    reservationId: string
  ): Promise<ApiResponse<ResponseModel<ReservationActivityModel[]>, ErrorInfo>> =>
    api.get(`/reservations/${reservationId}/changes`);

  const reservationRoomSwitch = (
    reservationId: number,
    sourceUnitTypeId: number,
    destinationUnitTypeId: number,
    roomId: string
  ): Promise<ApiResponse<ResponseModel<ReservationDetailsModel>, ErrorInfo>> =>
    api.post(`/reservations/${reservationId}/switch`, {
      currentUnitTypeId: sourceUnitTypeId,
      newUnitTypeId: destinationUnitTypeId,
      newRoomId: roomId,
      skipValidation: true
    });

  const getReservationsUpdates = () =>
    api.get<ResponseModel<ReservationUpdates>, ErrorInfo>(`reservations/updates `);

  const addPropertyManagerToReservations = (
    id: string,
    reservationId: string
  ): Promise<ApiResponse<ResponseModel<ReservationModel>, ErrorInfo>> =>
    api.post('reservations/addPropertyManagers', { id, reservationId });

  const deletePropertyManagerToReservations = (
    id: string,
    reservationId: string
  ): Promise<ApiResponse<ResponseModel<ReservationModel>, ErrorInfo>> =>
    api.delete(`reservations/deletePropertyManagers?id=${id}&reservationId=${reservationId}`);

  const getAllReservationsFeebacks = (query: FeedbackPayload) =>
    api.post<ResponseModel<{ count: number; data: FeedbackResponse[] }>, ErrorInfo>(
      `reservations/feedback/search`,
      query
    );

  const getOneReservationsFeebacks = (reservationId: string) =>
    api.get<ResponseModel<FeedbackResponse>, ErrorInfo>(`reservations/feedback/${reservationId}`);

  const downloadFeedback = (feedbackId: string) =>
    api.get<ResponseModel<FeedbackResponse>, ErrorInfo>(
      `reservations/feedback/${feedbackId}/export`
    );

  // Guest APIs
  const getOneGuest = (id: string): Promise<ApiResponse<ResponseModel<GuestModel>, ErrorInfo>> =>
    api.get(`/guests/${id}`);

  const updateGuest = (
    guest: GuestUiModel
  ): Promise<ApiResponse<ResponseModel<GuestModel>, ErrorInfo>> =>
    api.put(`/guests/${guest.id}`, guest);

  const deleteGuest = (
    guestId: string
  ): Promise<ApiResponse<ResponseModel<GuestModel>, ErrorInfo>> => api.delete(`/guests/${guestId}`);

  const addGuest = (
    guest: GuestUiModel
  ): Promise<ApiResponse<ResponseModel<GuestModel>, ErrorInfo>> => api.post('/guests', guest);

  const uploadGuestDocument = (
    formData: FormData
  ): Promise<ApiResponse<{ data: any }, ErrorInfo>> => api.post('guests/uploadDocument', formData);

  const getGuests = (
    query: {},
    offset: number,
    limit: number
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post('/guests/filter', { query, offset, limit });

  // Payment APIs
  const updateOnePayment = (
    payment: PaymentUIModel
  ): Promise<ApiResponse<ResponseModel<PaymentModel>, ErrorInfo>> =>
    api.put(`/payments/${payment.id}`, payment);

  const createOnePayment = (
    payment: PaymentUIModel
  ): Promise<ApiResponse<ResponseModel<PaymentModel>, ErrorInfo>> => api.post('/payments', payment);

  const generatePaymentLink = (
    paymentLink: PaymentLinkModel
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post('/payments/generatePaymentLink', paymentLink);

  const sendPaymentLink = (
    paymentId: string,
    email: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post('/payments/sendPaymentLink', { paymentId, email });

  const checkPaymobPaymentStatus = (
    paymentId: string
  ): Promise<ApiResponse<ResponseModel<PaymentModel>, ErrorInfo>> =>
    api.get(`/payments/${paymentId}/checkPaymobStatus`);

  const uploadPaymentReceipt = (
    formData: FormData
  ): Promise<ApiResponse<{ data: any }, ErrorInfo>> =>
    api.post('reservations/uploadReceipt', formData);

  const paymentSearch = (query: Partial<PaymentSearch>, offset: number, limit: number) =>
    api.post<ResponseModel<PaymentModelData>, ErrorInfo>(`payments/search`, {
      query,
      offset,
      limit
    });

  const financialReportSearch = (query: FinancialReportQueryModel) =>
    api.post<any[], ErrorInfo>(`payments/generatePaymentReport`, query);

  const getUserPayments = (userId: string) =>
    api.post<ResponseModel<PaymentModel[]>, ErrorInfo>(`payments/paymentByUserId`, { userId });

  // Block Dates APIs
  const createBlockDates = (blockDates: CreateBlockDatesModel) =>
    api.post<ResponseModel<BlockDatesModel>, ErrorInfo>('blocked-dates', blockDates);

  const bulkUpdateBlockDates = (blockDates: CreateBlockDatesModel) =>
    api.post<ResponseModel<BlockDatesModel[]>, ErrorInfo>('blocked-dates/bulk', blockDates);

  const updateBlockDates = (id: number, blockDate: UpdateBlockDatesModel) =>
    api.put<ResponseModel<BlockDatesModel>, ErrorInfo>('blocked-dates/' + id, blockDate);

  const getBlockDates = (query: {}, offset: number, limit: number) =>
    api.post<ResponseModel<BlockDatesModel>, ErrorInfo>('blocked-dates/search', {
      ...query,
      offset,
      limit
    });

  // Unit Type Group APIs
  const getUnitTypeGroups = (): Promise<
    ApiResponse<ResponseModel<UnitTypeGroupModel[]>, ErrorInfo>
  > => api.get('/UnitTypeGroups/');

  const createUnitTypeGroup = (
    areaData: UnitTypeGroupModel
  ): Promise<ApiResponse<ResponseModel<UnitTypeGroupModel>, ErrorInfo>> =>
    api.post('/UnitTypeGroups/', areaData);

  const updateUnitTypeGroup = (
    unitTypeGroupModelId: string,
    unitTypeGroupModelData: UnitTypeGroupModel
  ): Promise<ApiResponse<ResponseModel<UnitTypeGroupModel>, ErrorInfo>> =>
    api.put('/UnitTypeGroups/' + unitTypeGroupModelId, unitTypeGroupModelData);

  const deleteUnitTypeGroup = (
    unitTypeGroupModelId: string
  ): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.delete('/UnitTypeGroups/' + unitTypeGroupModelId);

  const getUnitViews = (): Promise<
    ApiResponse<ResponseModel<{ id: string; name: string }[]>, ErrorInfo>
  > => api.get('/views/');

  const getTypeOfUnit = (): Promise<
    ApiResponse<ResponseModel<{ id: string; name: string }[]>, ErrorInfo>
  > => api.get('/type-of-units/');

  const getBuilding = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/buildings/');

  // Report APIs
  const getMonthlyRevenue = (monthlycount: number) =>
    api.get<ResponseModel<BookAvailabilityResponseModel[]>, ErrorInfo>(
      `report/monthlyRevenue?months=${monthlycount}`
    );

  const getMonthlyRevenueWithCurrency = (monthlyCount: number, payload: KPIChartsSearch) =>
    api.post<
      ResponseModel<
        { month: string; egpRevenue: number; usdRevenue: number; totalRevenu: number }[]
      >,
      ErrorInfo
    >(`report/monthlyRevenue-currency?months=${monthlyCount}`, payload);

  const getOccupancyAndAverageDailyRate = (monthlyCount: number, payload: KPIChartsSearch) =>
    api.post<
      ResponseModel<{ month: string; rate: number; ADR: number; RPAR: number }[]>,
      ErrorInfo
    >(`report/total-occupancy?months=${monthlyCount}`, payload);

  const getRevenuePerArea = (payload: KPIChartsSearch) =>
    api.post<ResponseModel<{ area: string; share: number }[]>, ErrorInfo>(`report/area`, payload);

  const getInternationAndGuest = (payload: KPIChartsSearch) =>
    api.post<ResponseModel<{ egyptians: number; foreigners: number }>, ErrorInfo>(
      `report/nationality`,
      payload
    );

  const getSourceOfBooking = (monthlyCount: number, payload: KPIChartsSearch) =>
    api.post<
      ResponseModel<{ month: string; salesChannels: number; birdnest: number }[]>,
      ErrorInfo
    >(`report/source-of-booking?months=${monthlyCount}`, payload);

  const getOccupancy = () =>
    api.get<ResponseModel<OccupancyResponseModel[]>, ErrorInfo>(`report/occupancy`);

  // Expense & Payout APIs
  const createExpenses = (expense: CreateExpenses) =>
    api.post<ResponseModel<Expenses>, ErrorInfo>(`expenses`, expense);

  const deleteExpenses = (expenseId: string) =>
    api.delete<ResponseModel<Expenses>, ErrorInfo>(`expenses/${expenseId}`);

  const uploadExpense = (expenseFile: FormData) =>
    api.post<ResponseModel<Expenses>, ErrorInfo>(`expenses/uploadReceipt`, expenseFile);

  const getExpenses = (query: {}) =>
    api.post<ResponseModel<Expenses[]>, ErrorInfo>(`expenses/search`, query);

  const createPayout = (payout: CreatePayOutDto) =>
    api.post<ResponseModel<Payout>, ErrorInfo>(`payout`, payout);

  const uploadPayout = (payoutFile: FormData) =>
    api.post<ResponseModel<Payout>, ErrorInfo>(`payout/uploadReceipt`, payoutFile);

  const deletePayout = (payoutId: string) =>
    api.delete<ResponseModel<Payout>, ErrorInfo>(`payout/${payoutId}`);

  const searchPayouts = (payout: SearchPayOutDto) =>
    api.post<ResponseModel<Payout[]>, ErrorInfo>(`payout/search`, payout);

  const getPayouts = (query: {}) =>
    api.post<ResponseModel<Payout[]>, ErrorInfo>(`payout/search`, query);

  const searchHousekeepingOrders = (query: {
    offset?: number;
    limit?: number;
    project?: string;
    unitId?: string;
    projectId?: string;
    areaId?: string;
    unit?: string;
    orderDateFrom?: number;
    orderDateTo?: number;
  }) => api.post<ResponseModel<any[]>, ErrorInfo>(`/housekeeping-orders/search`, query);

  const updateHousekeepingOrder = (
    orderId: string,
    data: {
      orderDate?: number;
      status?: string;
      houseKeepingIds?: string[];
    }
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/housekeeping-orders/${orderId}`, data);

  const assignHousekeepingOrderToSelf = (
    orderId: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/housekeeping-orders/${orderId}/assign-self`, {});

  const assignHousekeepingUsers = (data: {
    unitTypeIds?: string[];
    projectId?: string;
    assignedToId: string;
  }): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post(`/housekeeping-orders/assign-users`, data);

  const createHousekeepingOrder = (data: {
    projectId: string;
    unitTypeIds: string[];
    houseKeepingIds?: string[];
    orderDate: number;
  }): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post(`/housekeeping-orders`, data);

  //TODO: change this
  const uploadHousekeepingOrderImages = (
    orderId: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post(`/housekeeping-orders/${orderId}/Images`, formData);

  // Configuration APIs
  const getConfiguration = (): Promise<
    ApiResponse<ResponseModel<ConfigurationModel[]>, ErrorInfo>
  > => api.get('/config');

  const updateConfiguration = (
    configuration: ConfigurationUiModel[]
  ): Promise<ApiResponse<ResponseModel<ConfigurationModel[]>, ErrorInfo>> =>
    api.put('/config', { configs: configuration });

  // Furnishing APIs
  const furnishingRequestsGet = (params: {
    offset?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/furnishing-requests', params);

  const furnishingRequestUpdate = (
    id: string,
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/furnishing-requests/${id}`, data);

  const furnishingRequestCreate = (
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/furnishing-requests', data);

  const furnishingRequestStatusEventsGet = (
    furnishingRequestId: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.get(`/furnishing-request-status-events/furnishing-request/${furnishingRequestId}`);

  const furnishingPackagesGet = (): Promise<
    ApiResponse<ResponseModel<PackageModel[]>, ErrorInfo>
  > => api.get('/packages');

  const furnishingModelsGet = (): Promise<
    ApiResponse<ResponseModel<FurnishingModel[]>, ErrorInfo>
  > => api.get('/models');

  const furnishingPackageCreate = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<PackageModel>, ErrorInfo>> =>
    api.post('/packages', formData);

  const furnishingPackageUpdate = (
    id: string,
    formData: any
  ): Promise<ApiResponse<ResponseModel<PackageModel>, ErrorInfo>> =>
    api.put(`/packages/${id}`, formData);

  const furnishingPackageDelete = (
    id: string
  ): Promise<ApiResponse<ResponseModel<PackageModel>, ErrorInfo>> => api.delete(`/packages/${id}`);

  const furnishingModelCreate = (
    modelData: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post(`/models`, modelData);

  const furnishingModelUpdate = (
    modelId: string,
    modelData: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/models/${modelId}`, modelData);

  const furnishingModelDelete = (
    modelId: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.delete(`/models/${modelId}`);

  const furnishingShowRoomsGet = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/show-rooms');

  const furnishingRoomCreate = (
    showRoomData: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/show-rooms', showRoomData);

  const furnishingRoomDelete = (
    showRoomId: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.delete(`/show-rooms/${showRoomId}`);

  const furnishingRoomUpdate = (
    showRoomId: string,
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/show-rooms/${showRoomId}`, data);

  const furnishingItemSpecCreate = (
    itemSpecData: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/item-specs', itemSpecData);

  const furnishingItemSpecsGet = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/item-specs');

  const furnishingItemSpecDelete = (
    itemSpecId: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.delete(`/item-specs/${itemSpecId}`);

  const furnishingItemSpecUpdate = (
    itemSpecId: string,
    itemSpecData: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/item-specs/${itemSpecId}`, itemSpecData);

  const furnishingItemSpecImageUpdate = (
    itemSpecId: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/item-specs/${itemSpecId}/image`, formData);

  const furnishingPricesCreate = (pricesData: {
    prices: Array<{
      packageId: string;
      modelId: string;
      unitTypeId: number;
      price: number;
      addons?: string;
    }>;
  }): Promise<
    ApiResponse<
      ResponseModel<
        { packageId: string; modelId: string; unitTypeId: number; price: number; addons?: string }[]
      >,
      ErrorInfo
    >
  > => api.post(`/offers`, { offers: pricesData?.prices });

  const furnishingOffersUpdate = (
    offerId: string,
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.put(`/offers/${offerId}`, data);

  const furnishingOffersGet = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/offers');

  const furnishingOffersDelete = (
    offerId: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.delete(`/offers/${offerId}`);

  const furnishingModelImageUpdate = (
    modelId: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/models/${modelId}/image`, formData);

  const furnishingPackageImageUpdate = (
    packageId: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/packages/${packageId}/image`, formData);

  const furnishingRoomImageUpdate = (
    showRoomId: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/show-rooms/${showRoomId}/images`, formData);

  const furnishingUnitTypes = (): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.get('/unit-types');

  const furnishingUnitTypesWebsite = (): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.get('/unitTypes/group/meta ');

  const furnishingAddOnsCreate = (data: {
    packageId: number;
    unitTypeId: string;
    addons: Array<{ name: string; nameAr: string; price: number; order?: number }>;
  }): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/addons', data);

  const furnishingAddOnsUpdate = (
    addonId: string,
    data: { set: { id: string; name: string; nameAr: string; price: number } }
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.put(`/addons/${addonId}`, data);

  const furnishingAddOnsDelete = (
    addonId: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.delete(`/addons/${addonId}`);

  const furnishingAddOnsGet = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/addons');

  const furnishingPortfolioGet = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/portfolio');

  const furnishingPortfolioCreate = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/portfolio', formData);

  const furnishingPortfolioDelete = (
    id: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.delete(`/portfolio/${id}`);

  const furnishingPortfolioUpdate = (
    id: string,
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.put(`/portfolio/${id}`, data);

  const furnishingPortfolioImageUpdate = (
    id: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/portfolio/${id}/image`, formData);

  const furnishingPortfolioRoomsGet = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/portfolio-rooms');

  const furnishingPortfolioRoomsCreate = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/portfolio-rooms', formData);

  const furnishingPortfolioRoomsUpdate = (
    id: string,
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.put(`/portfolio-rooms/${id}`, data);

  const furnishingPortfolioRoomsImageUpdate = (
    id: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/portfolio-rooms/${id}/image`, formData);

  const furnishingPortfolioReviewsGet = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/portfolio-reviews');

  const furnishingPortfolioReviewCreate = (
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/portfolio-reviews', data);

  const furnishingPortfolioReviewUpdate = (
    id: string,
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/portfolio-reviews/${id}`, data);

  const furnishingPortfolioReviewDelete = (
    id: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.delete(`/portfolio-reviews/${id}`);

  const getOccupancyRate = (
    query: {},
    isExport?: boolean
  ): Promise<ApiResponse<ResponseModel<ReservationModelData>, ErrorInfo>> =>
    api.post('/report/v2/total-occupancy', { query, isExport });

  // Auto-set displayName for all functions (for error tracking)
  const apiMethods = {
    getRoot,
    registerUser,
    loginUser,
    updateUser,
    changePassword,
    forgotPassword,
    getUserNotifications,
    uploadPropertyAsset,
    uploadPropertyCoverImage,
    uploadCompoundDetails,
    deleteCompoundDetails,
    updateCompoundDetails,
    getAreas,
    getCites,
    getUnitTypeGroups,
    getUnitViews,
    getTypeOfUnit,
    getBuilding,
    createArea,
    updateArea,
    deleteArea,
    getProperties,
    createProperty,
    updateProperty,
    deleteProperty,
    deleteBrandingImage,
    uploadBrandingImage,
    getProperty,
    getPropertyUnitTypes,
    getUnassignedUnitTypes,
    createUnitType,
    getUnitTypes,
    searchInUnitTypes,
    getUnitType,
    getUserReservations,
    updateUnitType,
    syncUnitTypeChannel,
    getUserPayments,
    deleteUnitTypeShowroomImage,
    uploadUnitTypeShowRoomImage,
    getAmenities,
    getChannelAmenities,
    getChannelRoomAmenities,
    getCompanies,
    createCompany,
    updateCompany,
    deleteCompany,
    getAllUsers,
    addUser,
    createAmenity,
    updateAmenity,
    deleteAmenity,
    uploadAmenityImage,
    uploadUnitTypeImage,
    deleteUnitTypeImage,
    getBookingRequests,
    updateBookingRequests,
    getBookingRequestsByQuery,
    getUserBookings,
    getBookingRequestDetails,
    acceptBookingRequest,
    validateToken,
    getReservations,
    getReservationGes,
    getCxQueue,
    updateCxQueue,
    getReservationsOperationsSearch,
    createReservationOperationMaintenance,
    searchMaintenanceRequests,
    updateMaintenanceRequest,
    getOperationDashboardSummary,
    getDoubleBookings,
    getOneReservation,
    updateOneReservation,
    updateReservationGes,
    updateReservationDates,
    getOneGuest,
    updateGuest,
    deleteGuest,
    addGuest,
    postReservationMessage,
    getReservationActivities,
    getPaymentActivities,
    getReservationChanges,
    reservationRoomSwitch,
    updateOnePayment,
    createOnePayment,
    generatePaymentLink,
    sendPaymentLink,
    checkPaymobPaymentStatus,
    uploadPaymentReceipt,
    uploadGuestDocument,
    getBookAvailability,
    getBookOffer,
    createReservations,
    createBlockDates,
    bulkUpdateBlockDates,
    createNewBookingRequest,
    updateBlockDates,
    getBlockDates,
    reactivateReservation,
    createUnitTypeGroup,
    updateUnitTypeGroup,
    deleteUnitTypeGroup,
    submitBookingMessage,
    uploadSponsoredImage,
    uploadDefaultImage,
    rejectBookingRequest,
    getMonthlyRevenue,
    getMonthlyRevenueWithCurrency,
    getOccupancyAndAverageDailyRate,
    getRevenuePerArea,
    getInternationAndGuest,
    getSourceOfBooking,
    getReservationsUpdates,
    getOccupancy,
    paymentSearch,
    financialReportSearch,
    createExpenses,
    deleteExpenses,
    uploadExpense,
    createPayout,
    uploadPayout,
    deletePayout,
    searchPayouts,
    getPayouts,
    searchHousekeepingOrders,
    updateHousekeepingOrder,
    assignHousekeepingOrderToSelf,
    assignHousekeepingUsers,
    createHousekeepingOrder,
    uploadHousekeepingOrderImages,
    getExpenses,
    searchUser,
    getUserLockStatus,
    unlockUser,
    refreshToken,
    getConfiguration,
    updateConfiguration,
    addPropertyManager,
    deletePropertyManager,
    getAllRooms,
    bulkUpdateUnitType,
    getAllBulkUpdateUnitTypeData,
    addPropertyManagerToReservations,
    deletePropertyManagerToReservations,
    addPropertyManagerToUnits,
    deletePropertyManagerToUnits,
    getAllReservationsFeebacks,
    getOneReservationsFeebacks,
    downloadFeedback,
    getUsers,
    getGuests,
    furnishingPackageCreate,
    furnishingPackageDelete,
    furnishingPackageUpdate,
    furnishingPackagesGet,
    furnishingRequestsGet,
    furnishingRequestCreate,
    furnishingRequestUpdate,
    furnishingRequestStatusEventsGet,
    furnishingModelsGet,
    furnishingModelCreate,
    furnishingModelUpdate,
    furnishingModelDelete,
    furnishingShowRoomsGet,
    furnishingRoomCreate,
    furnishingRoomDelete,
    furnishingRoomUpdate,
    furnishingItemSpecCreate,
    furnishingItemSpecsGet,
    furnishingItemSpecDelete,
    furnishingItemSpecUpdate,
    furnishingItemSpecImageUpdate,
    furnishingPricesCreate,
    furnishingOffersUpdate,
    furnishingOffersGet,
    furnishingOffersDelete,
    furnishingModelImageUpdate,
    furnishingPackageImageUpdate,
    furnishingRoomImageUpdate,
    furnishingUnitTypes,
    furnishingUnitTypesWebsite,
    furnishingAddOnsCreate,
    furnishingAddOnsUpdate,
    furnishingAddOnsDelete,
    furnishingAddOnsGet,
    furnishingPortfolioGet,
    furnishingPortfolioCreate,
    furnishingPortfolioDelete,
    furnishingPortfolioUpdate,
    furnishingPortfolioImageUpdate,
    furnishingPortfolioRoomsGet,
    furnishingPortfolioRoomsCreate,
    furnishingPortfolioRoomsUpdate,
    furnishingPortfolioRoomsImageUpdate,
    furnishingPortfolioReviewsGet,
    furnishingPortfolioReviewCreate,
    furnishingPortfolioReviewUpdate,
    furnishingPortfolioReviewDelete,
    getOccupancyRate,
    uploadReservationGesInspectionImages
  };

  // Auto-assign displayName to all methods
  Object.keys(apiMethods).forEach((key) => {
    const method = apiMethods[key as keyof typeof apiMethods];
    if (typeof method === 'function') {
      (method as any).displayName = key;
    }
  });

  return apiMethods;
};

// const api = debugConfig.useFixtures ? fixtureApiService : create();
const api = create();

export default api;
export type ApiType = typeof api;
